import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { teamApi } from '../../services/api';
import TeamForm from '../../components/teams/TeamForm';
import { Loader, AlertTriangle, Shield, ArrowLeft } from 'lucide-react';

const EditTeamPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [team, setTeam] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchTeam = async () => {
      try {
        const response = await teamApi.getTeam(id);
        setTeam(response.data);
      } catch (err) {
        setError('Failed to fetch team data.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchTeam();
  }, [id]);

  const handleSubmit = async (teamData) => {
    setIsSaving(true);
    setError(null);
    try {
      await teamApi.updateTeam(id, teamData);
      navigate('/teams');
    } catch (err) {
      setError('Failed to save team. Please try again.');
      console.error(err);
      setIsSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-900 via-blue-950 to-slate-800 flex items-center justify-center">
        <div className="text-center">
          <div className="w-14 h-14 rounded-full bg-gradient-to-br from-royal-blue to-blue-400 flex items-center justify-center mx-auto mb-4 shadow-xl">
            <Loader className="h-7 w-7 text-white animate-spin" />
          </div>
          <p className="text-gray-400 font-medium">Loading team...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 via-blue-950 to-slate-800">

      {/* Dark hero header */}
      <div className="bg-gradient-to-r from-gray-900 via-blue-950 to-gray-900 border-b-4 border-yellow-400">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <button
            onClick={() => navigate('/teams')}
            className="inline-flex items-center gap-2 text-gray-400 hover:text-white text-sm font-medium mb-5 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />Back to My Teams
          </button>
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-yellow-400 flex items-center justify-center shadow-lg shrink-0">
              <Shield className="h-6 w-6 text-gray-900" />
            </div>
            <div>
              <span className="text-xs font-bold text-yellow-400 uppercase tracking-widest">Teams</span>
              <h1 className="text-2xl sm:text-3xl font-black text-white tracking-tight">
                Edit {team?.teamName || 'Team'}
              </h1>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {error && (
          <div className="bg-red-900/30 border border-red-500/30 rounded-2xl p-4 flex items-center gap-3 text-red-400 mb-6">
            <AlertTriangle className="h-5 w-5 shrink-0" />
            <span className="text-sm font-medium">{error}</span>
          </div>
        )}
        {team && <TeamForm onSubmit={handleSubmit} initialData={team} isSaving={isSaving} />}
      </div>
    </div>
  );
};

export default EditTeamPage;
